import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

const STORAGE_KEY = "berqelo_cookie_consent_v1";

function applyConsent(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function useCookieBanner() {
  const [openBanner, setOpenBanner] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setOpenBanner(true);
    } else {
      applyConsent(stored);
    }
  }, []);

  const open = () => {
    setOpenSettings(true);
  };

  return { openBanner, setOpenBanner, openSettings, setOpenSettings, open };
}

export default function CookieBanner({
  openBanner,
  setOpenBanner,
  openSettings,
  setOpenSettings,
}) {
  const { t, buildLocalizedPath } = useI18n();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const persist = (consent) => {
    const payload = { ...consent, ts: Date.now() };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
    applyConsent(payload);
  };

  const acceptAll = () => {
    persist({ necessary: true, analytics: true, marketing: true });
    setOpenBanner(false);
    setOpenSettings(false);
  };

  const rejectAll = () => {
    persist({ necessary: true, analytics: false, marketing: false });
    setOpenBanner(false);
    setOpenSettings(false);
  };

  const saveChoices = () => {
    persist({ necessary: true, analytics, marketing });
    setOpenBanner(false);
    setOpenSettings(false);
  };

  if (!openBanner && !openSettings) return null;

  return (
    <>
      {openBanner && !openSettings && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 border-t border-stone-800"
          style={{ backgroundColor: "#1C241C", color: "#FAF8F5" }}
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 grid gap-6 md:grid-cols-[1fr_auto] items-center">
            <div>
              <div className="font-serif text-xl" style={{ color: "#FAF8F5" }}>
                {t("cookie_banner.title")}
              </div>
              <p className="mt-3 font-sans text-sm max-w-3xl" style={{ color: "#B8B1A3" }}>
                {t("cookie_banner.text")}{" "}
                <Link
                  to={buildLocalizedPath("cookie")}
                  className="underline"
                  style={{ color: "#C59B27" }}
                  data-testid="cookie-banner-policy-link"
                >
                  {t("cookie_banner.link_policy")}
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setOpenSettings(true)}
                data-testid="cookie-banner-manage"
                className="rounded-sm border border-stone-500 px-4 py-2 font-sans text-sm"
                style={{ color: "#FAF8F5" }}
              >
                {t("cookie_banner.manage")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                className="rounded-sm border border-stone-500 px-4 py-2 font-sans text-sm"
                style={{ color: "#FAF8F5" }}
              >
                {t("cookie_banner.reject_all")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                className="rounded-sm px-4 py-2 font-sans text-sm"
                style={{ backgroundColor: "#C59B27", color: "#1C241C" }}
              >
                {t("cookie_banner.accept_all")}
              </button>
            </div>
          </div>
        </div>
      )}

      {openSettings && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="cookie-settings-modal"
        >
          <div
            className="w-full max-w-2xl rounded-sm border border-stone-300 p-8"
            style={{ backgroundColor: "#FAF8F5", color: "#1C1917" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#8A8478" }}>
                  Berqelo
                </div>
                <h2 className="mt-2 font-serif text-2xl">{t("cookie_banner.settings_title")}</h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenSettings(false);
                  if (!readStored()) setOpenBanner(true);
                }}
                aria-label={t("aria.close")}
                data-testid="cookie-settings-close"
                className="font-mono text-sm"
              >
                ×
              </button>
            </div>

            <ul className="mt-6 space-y-5">
              <li className="border-b border-stone-200 pb-4">
                <div className="flex items-center justify-between">
                  <div className="font-sans font-semibold">{t("cookie_banner.category_necessary")}</div>
                  <span
                    className="font-mono text-[11px] uppercase tracking-[0.2em]"
                    style={{ color: "#57534E" }}
                  >
                    ✓
                  </span>
                </div>
                <p className="mt-2 font-sans text-sm" style={{ color: "#57534E" }}>
                  {t("cookie_banner.category_necessary_desc")}
                </p>
              </li>
              <li className="border-b border-stone-200 pb-4">
                <div className="flex items-center justify-between">
                  <div className="font-sans font-semibold">{t("cookie_banner.category_analytics")}</div>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      data-testid="cookie-settings-analytics"
                    />
                  </label>
                </div>
                <p className="mt-2 font-sans text-sm" style={{ color: "#57534E" }}>
                  {t("cookie_banner.category_analytics_desc")}
                </p>
              </li>
              <li className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="font-sans font-semibold">{t("cookie_banner.category_marketing")}</div>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                      data-testid="cookie-settings-marketing"
                    />
                  </label>
                </div>
                <p className="mt-2 font-sans text-sm" style={{ color: "#57534E" }}>
                  {t("cookie_banner.category_marketing_desc")}
                </p>
              </li>
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-settings-reject"
                className="rounded-sm border border-stone-400 px-4 py-2 font-sans text-sm"
              >
                {t("cookie_banner.reject_all")}
              </button>
              <button
                type="button"
                onClick={saveChoices}
                data-testid="cookie-settings-save"
                className="rounded-sm border border-stone-800 px-4 py-2 font-sans text-sm"
                style={{ backgroundColor: "#1C241C", color: "#FAF8F5" }}
              >
                {t("cookie_banner.save")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-settings-accept"
                className="rounded-sm px-4 py-2 font-sans text-sm"
                style={{ backgroundColor: "#C59B27", color: "#1C241C" }}
              >
                {t("cookie_banner.accept_all")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
